import React from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FONT_FAMILY } from 'common/sharedFunctions';

export default function DashboardCard({
  title,
  value,
  subtitle,
  badge,
  badgeType = 'neutral', // 'success' | 'warning' | 'info' | 'neutral'
  icon,
  action,
  sx
}) {
  const getBadgeColors = () => {
    switch (badgeType) {
      case 'success':
        return { bg: '#ECFDF5', color: '#059669', border: '#A7F3D0' };
      case 'warning':
        return { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' };
      case 'info':
        return { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' };
      case 'danger':
        return { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' };
      default:
        return { bg: '#F1F5F9', color: '#475569', border: '#E2E8F0' };
    }
  };

  const badgeStyle = getBadgeColors();

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid #E2E8F0',
        padding: '20px 22px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: '#CBD5E1',
          boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.07)',
          transform: 'translateY(-2px)'
        },
        ...sx
      }}
    >
      {/* ── Card Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: '0.81rem',
            fontWeight: 600,
            color: '#64748B',
            letterSpacing: '0.1px'
          }}
        >
          {title}
        </Typography>

        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#475569'
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      {/* ── Main Metric Value ── */}
      <Box sx={{ mb: 1.2 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#0F172A',
            letterSpacing: '-0.03em',
            lineHeight: 1.15
          }}
        >
          {value}
        </Typography>
      </Box>

      {/* ── Card Footer with Context Badge ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 1, borderTop: '1px solid #F8FAFC' }}>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: FONT_FAMILY,
              color: '#94A3B8',
              fontSize: '0.73rem',
              fontWeight: 500
            }}
          >
            {subtitle}
          </Typography>
        )}

        {badge && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.3,
              borderRadius: '6px',
              backgroundColor: badgeStyle.bg,
              border: `1px solid ${badgeStyle.border}`,
              color: badgeStyle.color,
              fontSize: '0.7rem',
              fontWeight: 700,
              fontFamily: FONT_FAMILY,
              letterSpacing: '0.2px'
            }}
          >
            {badge}
          </Box>
        )}

        {action}
      </Box>
    </Card>
  );
}


